/**
 * Shared utility for building auth headers.
 * Used by both axiosConfig.js interceptor and fetch-based clients (like SSE streaming).
 */

import { ZoomCloud } from 'api/generated/types/zoomCloud';
import { useSupportUserStore } from 'stores/supportUserStore';
import { isSupportLoginEmail } from 'utils/supportUsers';
import { storage, StorageKey } from 'utils/typedStorage';

import { getTeamsToken } from './teamsSsoSession';

export {
  getTeamsSessionId,
  getTeamsToken,
  setTeamsToken,
} from './teamsSsoSession';

export type AuthHeaders = {
  Authorization?: string;
  'X-Impersonated-User-Email'?: string;
  'X-Full-Access'?: string;
  'microsoft-teams-auth-token'?: string;
  'google-meet-auth-token'?: string;
  'zoom-session-token'?: string;
  'org-id'?: string;
};

// Store for Teams token and org_id (handed over in memory by the Teams launcher)
let teamsOrgId: string | null = null;

// Store for Google Meet token and org_id (handed over in memory by the Meet launcher)
let googleMeetToken: string | null = null;
let googleMeetOrgId: string | null = null;

// Signed Zoom session, exchanged for the one-time code on the /zoom/launch
// redirect (or returned by the org picker). Held in memory and sent as the
// zoom-session-token header; the backend never sets or reads a cookie for it.
let zoomSessionToken: string | null = null;
let zoomSessionCloud: ZoomCloud = ZoomCloud.commercial;

/** Set the Zoom session token captured at launch. */
export function setZoomSessionToken(
  token: string | null,
  cloud: ZoomCloud = ZoomCloud.commercial
): void {
  zoomSessionToken = token;
  zoomSessionCloud = token ? cloud : ZoomCloud.commercial;
}

/** Cloud returned by the authenticated launch exchange, never a URL hint. */
export function getZoomSessionCloud(): ZoomCloud {
  return zoomSessionCloud;
}

/** Get the current Zoom session token (also used to authenticate the notes WS). */
export function getZoomSessionToken(): string | null {
  return zoomSessionToken;
}

/**
 * Set the Teams org_id for multi-org disambiguation
 */
export function setTeamsOrgId(orgId: string | null): void {
  teamsOrgId = orgId;
}

/**
 * Get the current Teams org_id
 */
export function getTeamsOrgId(): string | null {
  return teamsOrgId;
}

/**
 * Set the Google Meet token for auth fallback (when user is not logged in via Stytch)
 */
export function setGoogleMeetToken(token: string | null): void {
  googleMeetToken = token;
}

/**
 * Get the current Google Meet token
 */
export function getGoogleMeetToken(): string | null {
  return googleMeetToken;
}

/**
 * Set the Google Meet org_id for multi-org disambiguation
 */
export function setGoogleMeetOrgId(orgId: string | null): void {
  googleMeetOrgId = orgId;
}

/**
 * Get the current Google Meet org_id
 */
export function getGoogleMeetOrgId(): string | null {
  return googleMeetOrgId;
}

/**
 * Whether the user is signed in via normal auth (an authToken in localStorage).
 * Storage access throws in some privacy modes and sandboxed frames; that reads as
 * signed out rather than failing the caller.
 */
export function isNormalAuthAvailable(): boolean {
  return getNormalAuthToken() !== null;
}

/**
 * The normal-auth token from localStorage, or null when signed out. Empty
 * strings read as signed out, and storage access that throws (privacy modes,
 * sandboxed frames) reads as null rather than failing the caller.
 */
export function getNormalAuthToken(): string | null {
  return storage.get(StorageKey.AuthToken) || null;
}

/**
 * Put normal auth on a WebSocket URL: a WebSocket cannot send headers, so the
 * token and any support-user impersonation travel as query params. Returns
 * false (and leaves the URL untouched) when the user is not signed in via
 * normal auth, so the caller can fall back to another auth mode.
 */
export function applyNormalAuthToWebSocketUrl(url: URL): boolean {
  const token = getNormalAuthToken();
  if (token === null) {
    return false;
  }
  url.searchParams.set('token', token);
  const { isSupportUser, fullAccessEnabled, impersonatedEmail } =
    useSupportUserStore.getState();
  if (isSupportUser && impersonatedEmail) {
    url.searchParams.set('impersonated_email', impersonatedEmail);
    if (fullAccessEnabled) {
      url.searchParams.set('full_access', 'true');
    }
  }
  return true;
}

/**
 * Build auth headers from store and localStorage values.
 * Includes Authorization, X-Impersonated-User-Email, and X-Full-Access (for support users).
 * Falls back to Teams token auth when normal auth is not available.
 *
 * Note: This reads from the Zustand store for support user state, which is synced with localStorage.
 */
export function getAuthHeaders(): AuthHeaders {
  const headers: AuthHeaders = {};

  const authToken = storage.get(StorageKey.AuthToken);
  const teamsToken = getTeamsToken();

  // If user is authenticated via normal auth, use that
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;

    // Get support user state from the store
    const { isSupportUser, fullAccessEnabled, impersonatedEmail } =
      useSupportUserStore.getState();

    if (isSupportUser) {
      let effectiveImpersonatedEmail = impersonatedEmail;
      if (
        !effectiveImpersonatedEmail ||
        isSupportLoginEmail(effectiveImpersonatedEmail)
      ) {
        const storedEmail = storage.get(StorageKey.Email);
        if (storedEmail && !isSupportLoginEmail(storedEmail)) {
          effectiveImpersonatedEmail = storedEmail;
        }
      }

      if (effectiveImpersonatedEmail) {
        headers['X-Impersonated-User-Email'] = effectiveImpersonatedEmail;

        if (fullAccessEnabled) {
          headers['X-Full-Access'] = 'true';
        }
      }
    }
  } else if (teamsToken) {
    // Fall back to Teams token auth when user is not logged in
    headers['Authorization'] = `Bearer ${teamsToken}`;
    headers['microsoft-teams-auth-token'] = 'true';
    if (teamsOrgId) {
      headers['org-id'] = teamsOrgId;
    }
  } else if (googleMeetToken) {
    // Fall back to Google Meet token auth when user is not logged in
    headers['Authorization'] = `Bearer ${googleMeetToken}`;
    headers['google-meet-auth-token'] = 'true';
    if (googleMeetOrgId) {
      headers['org-id'] = googleMeetOrgId;
    }
  } else if (zoomSessionToken) {
    // Fall back to the Zoom session when no stronger auth is present. The
    // header is its only REST transport; there is no session cookie.
    headers['zoom-session-token'] = zoomSessionToken;
  }

  return headers;
}
